import { For, Show, createEffect, createMemo, createSignal } from "solid-js";

function validateEnum<T extends string>(value: unknown, options: readonly T[]): T | undefined {
  return (options as unknown as string[]).includes(value as string) ? (value as T) : undefined;
}

const TOURNAMENT_TABS = ["active", "upcoming", "past"] as const;
type TournamentTab = typeof TOURNAMENT_TABS[number];
import { TournamentFormFields } from "~/components/tournament-form-fields";
import { PageContainer, PageHeader } from "~/components/layout/page";
import { PendingChangesButton } from "~/components/pending-changes-button";
import { TournamentCard } from "~/components/tournament-card";
import { Button } from "~/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { playersApi, tournamentsApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { requireFeature } from "~/lib/features";
import { isTournamentEligible } from "~/lib/profile-utils";
import { createTrackList, emptyTrack, normalizeMultiplier, tracksFromArrays, tracksToArrays, type TrackEntry } from "~/lib/tracks";
import { TIER_COLORS, cn } from "~/lib/utils";

import { CAR_CLASSES, RACE_TYPES, TIERS, TIER_ORDER, type CarClass, type PromotionMode, type RaceType, type Tier, type Tournament } from "system";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { createFileRoute } from "@tanstack/solid-router";
import { QueryKey } from "~/lib/query-keys";
import { Plus } from "lucide-solid";
import { RulesDialog } from "~/components/rules-dialog";

export const Route = createFileRoute("/_authed/tournaments/")({
  beforeLoad: () => requireFeature("tournaments"),
  validateSearch: (search: Record<string, unknown>) => ({
    tab: validateEnum(search.tab, TOURNAMENT_TABS) ?? "upcoming" as TournamentTab,
  }),
  component: TournamentsPage,
});

function TournamentsPage() {
  const { state } = useAuth();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab = () => search().tab;
  const setTab = (t: string) => navigate({ search: (prev) => ({ ...prev, tab: t as TournamentTab }), replace: true });

  const tournaments = createQuery(() => ({
    queryKey: [QueryKey.Tournaments],
    queryFn: () => tournamentsApi.list(),
  }));

  const myProfile = createQuery(() => ({
    queryKey: [QueryKey.Profile, state.user?.id],
    queryFn: () => playersApi.get(state.user!.id),
    enabled: !!state.user?.id,
    staleTime: 60_000,
  }));

  const isEligible = (t: Tournament): boolean => {
    const profile = myProfile.data;
    if (!profile) return true;
    return isTournamentEligible(profile, t);
  };

  const byStatus = createMemo(() => {
    const all = tournaments.data ?? [];
    return {
      active: all.filter((t) => t.status === "active"),
      upcoming: all.filter((t) => t.status === "upcoming"),
      past: all.filter((t) => t.status === "completed" || t.status === "cancelled"),
    };
  });

  return (
    <PageContainer>
      <div class="flex items-start justify-between gap-4">
        <PageHeader title="Tournaments" description="Tier-based tournaments organized by admins" />
        <div class="shrink-0 pt-1 flex items-center gap-2">
          <RulesDialog scope="tournaments" />
          <PendingChangesButton />
          <Show when={state.user?.role === "admin"}>
            <CreateTournamentDialog />
          </Show>
        </div>
      </div>

      <Show when={tournaments.data} fallback={<GridSkeleton />}>
        <Tabs value={tab()} onChange={setTab}>
          <TabsList>
            <TabsTrigger value="upcoming">
              Upcoming
              <Show when={byStatus().upcoming.length > 0}>
                <span class="ml-1.5 px-1.5 py-0.5 text-label-xs font-bold rounded bg-surface-4 text-text-faint">{byStatus().upcoming.length}</span>
              </Show>
            </TabsTrigger>
            <TabsTrigger value="active">
              Active
              <Show when={byStatus().active.length > 0}>
                <span class="ml-1.5 px-1.5 py-0.5 text-label-xs font-bold rounded bg-brand/15 text-brand">{byStatus().active.length}</span>
              </Show>
            </TabsTrigger>
            <TabsTrigger value="past">
              Past
              <Show when={byStatus().past.length > 0}>
                <span class="ml-1.5 px-1.5 py-0.5 text-label-xs font-bold rounded bg-surface-4 text-text-faint">{byStatus().past.length}</span>
              </Show>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            <TierGroupedGrid
              tournaments={byStatus().upcoming}
              userId={state.user?.id}
              isEligible={isEligible}
              emptyText="No upcoming tournaments."
            />
          </TabsContent>

          <TabsContent value="active">
            <TierGroupedGrid
              tournaments={byStatus().active}
              userId={state.user?.id}
              isEligible={isEligible}
              emptyText="No active tournaments."
            />
          </TabsContent>

          <TabsContent value="past">
            <TierGroupedGrid
              tournaments={byStatus().past}
              userId={state.user?.id}
              isEligible={() => true}
              emptyText="No past tournaments."
              dim
            />
          </TabsContent>
        </Tabs>
      </Show>
    </PageContainer>
  );
}

function TierGroupedGrid(props: {
  tournaments: Tournament[];
  userId?: string;
  isEligible: (t: Tournament) => boolean;
  emptyText: string;
  dim?: boolean;
}) {
  // Group by the LOWEST accepted tier so low-activity tiers surface first
  // (members stay low because they lack time for duels, so we nudge them up).
  const lowestTier = (t: Tournament): Tier =>
    [...t.eligibleTiers].sort((a, b) => TIER_ORDER[a] - TIER_ORDER[b])[0] ?? "white";

  const groups = createMemo(() => {
    const map = new Map<Tier, Tournament[]>();
    for (const t of props.tournaments) {
      const key = lowestTier(t);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return TIERS.filter((tier) => map.has(tier)).map((tier) => ({ tier, items: map.get(tier)! }));
  });

  return (
    <Show when={groups().length > 0} fallback={<p class="text-center text-text-faint py-16">{props.emptyText}</p>}>
      <div class="space-y-6">
        <For each={groups()}>
          {(group) => (
            <section class="space-y-3">
              <h2 class={cn("font-display text-sm font-semibold uppercase tracking-widest", TIER_COLORS[group.tier].text)}>
                {group.tier.charAt(0).toUpperCase() + group.tier.slice(1)}
              </h2>
              <div class={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", props.dim && "opacity-60")}>
                <For each={group.items}>
                  {(t) => <TournamentCard tournament={t} userId={props.userId} eligible={props.isEligible(t)} />}
                </For>
              </div>
            </section>
          )}
        </For>
      </div>
    </Show>
  );
}

const DRAFT_KEY = "draft:create-tournament";
const PROMOTION_MODES = ["generous", "strict"] as const;

function loadTournamentDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// A fresh form starts with three blank races.
const defaultTracks = (): TrackEntry[] => [emptyTrack(), emptyTrack(), emptyTrack()];

// Restore the race list from a saved draft, accepting either the current
// {name, carType}[] shape or a legacy parallel-array draft, and falling back to
// three blank races.
function draftTracks(draft: any): TrackEntry[] {
  if (Array.isArray(draft?.tracks)) {
    return draft.tracks.map((t: any) => ({
      name: String(t?.name ?? ""),
      carType: String(t?.carType ?? ""),
      multiplier: normalizeMultiplier(t?.multiplier),
    }));
  }
  if (Array.isArray(draft?.trackNames)) {
    return tracksFromArrays(draft.trackNames, draft.carTypes, draft.raceMultipliers, draft.trackNames.length);
  }
  return defaultTracks();
}

function CreateTournamentDialog() {
  const draft = loadTournamentDraft();
  const [open, setOpen] = createSignal(false);
  const [name, setName] = createSignal(draft?.name ?? "");
  const [eligibleTiers, setEligibleTiers] = createSignal<Tier[]>(
    Array.isArray(draft?.eligibleTiers) && draft.eligibleTiers.length ? draft.eligibleTiers : [TIERS[0]],
  );
  const toggleTier = (t: Tier) =>
    setEligibleTiers((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const [raceType, setRaceType] = createSignal<RaceType>(validateEnum(draft?.raceType, RACE_TYPES) ?? RACE_TYPES[0]);
  const [carClass, setCarClass] = createSignal<CarClass>(validateEnum(draft?.carClass, CAR_CLASSES) ?? CAR_CLASSES[0]);
  const { tracks, setTracks, setTrackName, setCarType, setMultiplier, setAllCarTypes, addTrack, removeTrack } = createTrackList(draftTracks(draft));
  const [promotionMode, setPromotionMode] = createSignal<PromotionMode>(validateEnum(draft?.promotionMode, PROMOTION_MODES) ?? "generous");
  const [maxParticipants, setMaxParticipants] = createSignal(draft?.maxParticipants ?? "12");
  const [scheduledAt, setScheduledAt] = createSignal(draft?.scheduledAt ?? "");
  const qc = useQueryClient();

  createEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
        name: name(), eligibleTiers: eligibleTiers(), raceType: raceType(), carClass: carClass(),
        tracks: tracks(), promotionMode: promotionMode(),
        maxParticipants: maxParticipants(), scheduledAt: scheduledAt(),
      }));
    } catch {}
  });

  const create = createMutation(() => ({
    mutationFn: () => {
      return tournamentsApi.create({
        name: name(),
        eligibleTiers: eligibleTiers(),
        raceType: raceType(),
        carClass: carClass(),
        racesCount: tracks().length,
        ...tracksToArrays(tracks()),
        promotionMode: promotionMode(),
        maxParticipants: maxParticipants() ? parseInt(maxParticipants(), 10) : undefined,
        scheduledAt: scheduledAt() ? new Date(scheduledAt()).toISOString() : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Tournaments] });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
      setOpen(false);
      setName(""); setEligibleTiers([TIERS[0]]); setRaceType(RACE_TYPES[0]); setCarClass(CAR_CLASSES[0]);
      setTracks(defaultTracks()); setPromotionMode("generous"); setMaxParticipants("12"); setScheduledAt("");
    },
  }));

  return (
    <>
      {/* Mobile FAB — sits above the navbar */}
      <Button
        class="fixed bottom-20 left-4 z-50 sm:hidden gap-1.5 shadow-lg"
        onClick={() => setOpen(true)}
        aria-label="New Tournament"
      >
        <Plus size={16} /> New Tournament
      </Button>
      <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger as={Button} size="sm" class="gap-1.5 hidden sm:inline-flex">
        <Plus size={14} /> New Tournament
      </DialogTrigger>
      <DialogContent title="Create Tournament" class="sm:max-w-5xl">
        <DialogBody>
          <TournamentFormFields
            name={name()} onName={setName}
            eligibleTiers={eligibleTiers()} onToggleTier={toggleTier}
            raceType={raceType()} onRaceType={setRaceType}
            carClass={carClass()} onCarClass={setCarClass}
            tracks={tracks()} onTrackName={setTrackName} onCarType={setCarType} onMultiplier={setMultiplier} onAddTrack={addTrack} onRemoveTrack={removeTrack} onAllCarTypes={setAllCarTypes}
            promotionMode={promotionMode()} onPromotionMode={setPromotionMode}
            maxParticipants={maxParticipants()} onMaxParticipants={setMaxParticipants}
            scheduledAt={scheduledAt()} onScheduledAt={setScheduledAt}
          />
        </DialogBody>
        <Show when={create.error}>
          <p class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mt-4">{create.error?.message}</p>
        </Show>
        <div class="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!name().trim() || eligibleTiers().length === 0 || create.isPending}>
            {create.isPending ? "Creating..." : "Create Tournament"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

function GridSkeleton() {
  return (
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <For each={Array(6)}>{() => <div class="h-40 rounded-xl bg-surface-2 animate-pulse" />}</For>
    </div>
  );
}
